import { updateSocialMetaTags } from '/src/utils/MetaTagManager.js';
import gsap from 'gsap';

function generateTitleFromFilename(filename) {
    if (!filename || typeof filename !== 'string') return 'Burton Rast Photography';
    const base = filename.replace(/\.[^/.]+$/, '').replace(/[-_]+/g, ' ').trim();
    return base.replace(/\b\w/g, ch => ch.toUpperCase());
}

export class NavigationController {
    static LOGGING_ENABLED = false;
    constructor() {
        this.state = 'OVERVIEW'; // OVERVIEW, ZOOMED_IN, DETAIL_VIEW
        this.currentPosition = { row: 0, col: 0 };
        this.totalImages = 36;
        this.gridSize = { rows: 6, cols: 6 };
        this.components = {}; // Will hold references to other components
        this.isTransitioning = false;
        this._transitionTimeout = null;
        this.isReady = false;
        this._readyResolve = null;
        this.readyPromise = new Promise(resolve => {
            this._readyResolve = resolve;
        });
        
        // Queue for navigation commands received before components are ready
        this.navigationQueue = [];
        // Monotonic token to prevent stale async meta writes
        this._metaUpdateToken = 0;
        
        // Rapid navigation detection for instant keyboard response
        this.lastNavigationTime = 0;
        this.rapidNavigationMode = false;
        this.rapidNavigationThreshold = 250; // ms between commands to trigger rapid mode (increased for better detection)
        this.rapidNavigationCooldown = null;
        this.rapidKeyPressCount = 0; // Track consecutive rapid keypresses
    }

    // Set component references after construction
    setComponents(contactSheet, detailView, gestureManager) {
        this.components = { contactSheet, detailView, gestureManager };
        // Note: markAsReady() is now called from main.js after ContactSheet is fully initialized
    }

    // Set hamburger menu reference for keyboard blocking
    setHamburgerMenu(hamburgerMenu) {
        this.hamburgerMenu = hamburgerMenu;
    }

    // Set info modal reference for keyboard blocking and state sync
    setInfoModal(infoModal) {
        this.infoModal = infoModal;
    }

    markAsReady() {
        if (!this.isReady) {
            this.isReady = true;
            console.log('NC: All components initialized, ready for navigation');
            if (this._readyResolve) {
                this._readyResolve();
            }
            
            // Process any queued navigation commands
            if (this.navigationQueue.length > 0) {
                console.log('NC: Processing queued navigation commands');
                // Process queue immediately - no need for delay
                while (this.navigationQueue.length > 0) {
                    const { command, options, resolve } = this.navigationQueue.shift();
                    console.log(`NC: Processing queued command: ${command}`);
                    this.navigate(command, options).then(resolve);
                }
            }
        }
    }

    setupEventHandlers() {
        // Remove any existing keyboard handlers first
        this.removeEventHandlers();
        
        this.keyboardHandler = (event) => {
            // Block all keyboard navigation when hamburger menu is open
            if (this.hamburgerMenu?.isOpen) {
                return;
            }

            // Close info modal on Escape, block other keys while open
            if (this.infoModal?.isOpen) {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    this.infoModal.close();
                }
                return;
            }
            
            // Handle ContextMenu dismissal first
            if (this.components.contactSheet?.contextMenu?.isVisible) {
                this.components.contactSheet.contextMenu.hide();
                return;
            }

            // If a form input/textarea is focused, let it handle keys normally
            // except Escape, which blurs the input
            const activeEl = document.activeElement;
            const isFormInput = activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA');
            if (isFormInput) {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    activeEl.blur();
                }
                return; // Let form inputs handle all other keys
            }

            // Prevent default for navigation keys
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape'].includes(event.key)) {
                event.preventDefault();
            }

            switch (event.key) {
                case 'ArrowUp':
                    console.log(`NC: navigate('up') from keyboard.`);
                    this.navigate('up', { source: 'keyboard' });
                    break;
                case 'ArrowDown':
                    console.log(`NC: navigate('down') from keyboard.`);
                    this.navigate('down', { source: 'keyboard' });
                    break;
                case 'ArrowLeft':
                    console.log(`NC: navigate('left') from keyboard.`);
                    this.navigate('left', { source: 'keyboard' });
                    break;
                case 'ArrowRight':
                    console.log(`NC: navigate('right') from keyboard.`);
                    this.navigate('right', { source: 'keyboard' });
                    break;
                case 'Enter':
                case 'Return':
                    if (this.state === 'OVERVIEW') {
                        console.log(`NC: navigate('zoom-to-image') from keyboard.`);
                        this.navigate('zoom-to-image', { source: 'keyboard' });
                    } else if (this.state === 'ZOOMED_IN') {
                        this.navigate('detail-view');
                    }
                    break;
                case 'Escape':
                    if (this.state === 'DETAIL_VIEW') {
                        console.log(`NC: navigate('close-detail') from keyboard.`);
                        this.navigate('close-detail');
                    } else if (this.state === 'ZOOMED_IN') {
                        console.log('NC: navigate(\'zoom-out\') from keyboard.');
                        this.navigate('zoom-out');
                    }
                    break;
            }
        };

        document.addEventListener('keydown', this.keyboardHandler);
    }

    removeEventHandlers() {
        if (this.keyboardHandler) {
            document.removeEventListener('keydown', this.keyboardHandler);
        }
    }

    dispose() {
        this.removeEventHandlers();
        this._clearTransitionTimeout();
        if (this._zoomOutAfterDetailTimeout) {
            clearTimeout(this._zoomOutAfterDetailTimeout);
            this._zoomOutAfterDetailTimeout = null;
        }
    }

    // Safety valve: force-clear isTransitioning after 5s to prevent lockout
    _startTransitionTimeout() {
        this._clearTransitionTimeout();
        this._transitionTimeout = setTimeout(() => {
            if (this.isTransitioning) {
                console.error('NC: Transition timeout — forcing isTransitioning to false');
                this.isTransitioning = false;
                this.flushNavigationQueue();
            }
        }, 5000);
    }

    _clearTransitionTimeout() {
        if (this._transitionTimeout) {
            clearTimeout(this._transitionTimeout);
            this._transitionTimeout = null;
        }
    }

    // Gesture handlers - will be called by components
    handleTap(event) {
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController: tap in ${this.state}`);
        // Mark tutorial as learned for any user interaction
        this.handleTutorialInteraction();
        // Delegate to ContactSheet for now - full gesture handling will be implemented later
        return this.components.contactSheet?.handleTap(event);
    }

    handleSwipe(event) {
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController: swipe in ${this.state}`);
        // Mark tutorial as learned for any user interaction
        this.handleTutorialInteraction();
        // Delegate to ContactSheet for now - full gesture handling will be implemented later
        return this.components.contactSheet?.handleSwipe(event);
    }

    handlePinchStart(event) {
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController: pinch start in ${this.state}`);
        // Mark tutorial as learned for any user interaction
        this.handleTutorialInteraction();
        return this.components.contactSheet?.handlePinchStart(event);
    }

    handlePinchIn(event) {
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController: pinch in in ${this.state}`);
        // Mark tutorial as learned for any user interaction
        this.handleTutorialInteraction();
        return this.components.contactSheet?.handlePinchIn(event);
    }

    handlePinchOut(event) {
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController: pinch out in ${this.state}`);
        // Mark tutorial as learned for any user interaction
        this.handleTutorialInteraction();
        return this.components.contactSheet?.handlePinchOut(event);
    }

    handlePinchEnd(event) {
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController: pinch end in ${this.state}`);
        return this.components.contactSheet?.handlePinchEnd(event);
    }

    handlePanStart(event) {
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController: pan start in ${this.state}`);
        // Mark tutorial as learned for any user interaction
        this.handleTutorialInteraction();
        return this.components.contactSheet?.handlePanStart(event);
    }

    handlePanMove(event) {
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController: pan move in ${this.state}`);
        return this.components.contactSheet?.handlePanMove(event);
    }

    handlePanEnd(event) {
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController: pan end in ${this.state}`);
        return this.components.contactSheet?.handlePanEnd(event);
    }

    handleMultiTouchChange(isMultiTouch) {
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController: multi-touch ${isMultiTouch ? 'start' : 'end'} in ${this.state}`);
        return this.components.contactSheet?.handleMultiTouchChange(isMultiTouch);
    }

    // Main navigation interface
    async navigate(command, options = {}) {
        // If not ready yet, queue the navigation command
        if (!this.isReady) {
            console.log('NC: Loading state active, queuing navigation');
            return new Promise(resolve => {
                this.navigationQueue.push({ command, options, resolve });
            });
        }
        
        // Skip queueing if in rapid navigation mode for directional commands
        const isDirectional = ['up', 'down', 'left', 'right'].includes(command);
        if (this.rapidNavigationMode && isDirectional) {
            // Bypass queue for rapid navigation
            return Promise.resolve(this.handleDirectionalNav(command, options));
        }
        
        // If a transition is in progress, queue and execute after it completes
        if (this.isTransitioning) {
            return new Promise(resolve => {
                this.navigationQueue.push({ command, options, resolve });
            });
        }
        
        // Mark tutorial as learned for any user interaction
        this.handleTutorialInteraction();
        
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController: ${command} from ${this.state}`);
        
        switch (command) {
            case 'up': return Promise.resolve(this.handleDirectionalNav('up', options));
            case 'down': return Promise.resolve(this.handleDirectionalNav('down', options));
            case 'left': 
                // Log for non-keyboard sources (keyboard already logs before calling)
                if (options.source !== 'keyboard') {
                    if (options.source === 'swipe') {
                        console.log(`NC: navigate('left') from swipe.`);
                    } else {
                        console.log(`NC: navigate('left').`);
                    }
                }
                return Promise.resolve(this.handleDirectionalNav('left', options));
            case 'right': 
                // Log for non-keyboard sources (keyboard already logs before calling)
                if (options.source !== 'keyboard') {
                    if (options.source === 'swipe') {
                        console.log(`NC: navigate('right') from swipe.`);
                    } else {
                        console.log(`NC: navigate('right').`);
                    }
                }
                return Promise.resolve(this.handleDirectionalNav('right', options));
            case 'zoom-in': return this.handleZoomIn();
            case 'zoom-out': return this.handleZoomOut();
            case 'detail-view': return this.handleDetailView();
            // Context menu actions
            case 'zoom-to-image': return Promise.resolve(this.handleZoomToImage(options));
            case 'open-detail': 
                console.log(`NC: navigate('open-detail').`);
                return this.handleOpenDetail(options);
            case 'previous-image': return Promise.resolve(this.handlePreviousImage());
            case 'next-image': return Promise.resolve(this.handleNextImage());
            case 'close-detail': return this.handleCloseDetail(options);
            case 'back-to-contact': return this.handleBackToContact();
            default: return Promise.resolve(false);
        }
    }

    // Process queued navigation commands one-by-one when idle
    async flushNavigationQueue() {
        if (!this.isReady || this.isTransitioning) return;
        if (this.navigationQueue.length === 0) return;
        const { command, options, resolve } = this.navigationQueue.shift();
        try {
            const result = await this.navigate(command, options);
            if (typeof resolve === 'function') resolve(result);
        } finally {
            // Continue flushing if still idle
            if (!this.isTransitioning) {
                Promise.resolve().then(() => this.flushNavigationQueue());
            }
        }
    }

    handleDirectionalNav(direction, options = {}) {
        // Detect rapid navigation for keyboard input only
        const now = performance.now();
        const timeSinceLastNav = now - this.lastNavigationTime;

        // Only track timing and apply rapid mode for keyboard source
        if (options.source === 'keyboard' && this.state === 'ZOOMED_IN') {
            this.lastNavigationTime = now;

            // Enter rapid mode immediately on fast keypresses or if already in rapid mode
            if (timeSinceLastNav < this.rapidNavigationThreshold) {
                this.rapidKeyPressCount++;
                // Enter rapid mode after just 1 quick keypress (on the 2nd press)
                if (this.rapidKeyPressCount >= 1 || this.rapidNavigationMode) {
                    this.enterRapidNavigationMode();
                    return this.handleRapidGridNavigation(direction);
                }
            } else {
                // Reset count if too slow
                this.rapidKeyPressCount = 0;
                this.exitRapidNavigationMode();
            }
        }

        if (this.state === 'OVERVIEW') {
            return false;
        } else if (this.state === 'ZOOMED_IN') {
            return this.handleGridNavigation(direction);
        } else if (this.state === 'DETAIL_VIEW') {
            return this.handleLinearNavigation(direction);
        }
        return false;
    }
    
    enterRapidNavigationMode() {
        if (!this.rapidNavigationMode) {
            console.log('NC: Entering rapid navigation mode');
            this.rapidNavigationMode = true;
            // Clear any pending navigation queue
            this.navigationQueue = [];
            // Cancel cooldown timer
            if (this.rapidNavigationCooldown) {
                clearTimeout(this.rapidNavigationCooldown);
            }
            // Kill ALL animations to prevent interference
            if (this.components.contactSheet) {
                gsap.killTweensOf("*");
            }
        }
        
        // Reset cooldown - exit rapid mode after 300ms of no input
        if (this.rapidNavigationCooldown) {
            clearTimeout(this.rapidNavigationCooldown);
        }
        this.rapidNavigationCooldown = setTimeout(() => {
            this.exitRapidNavigationMode();
        }, 300);
    }
    
    exitRapidNavigationMode() {
        if (this.rapidNavigationMode) {
            console.log('NC: Exiting rapid navigation mode');
            this.rapidNavigationMode = false;
            this.rapidKeyPressCount = 0; // Reset count
            // Smooth settle to final position
            if (this.state === 'ZOOMED_IN' && this.components.contactSheet) {
                this.components.contactSheet.smoothSettleToPosition(this.currentPosition);
            }
        }
    }
    
    forceExitRapidNavigationMode() {
        if (this.rapidNavigationMode) {
            if (NavigationController.LOGGING_ENABLED) console.log('NC: Forcing exit rapid navigation mode');
            this.rapidNavigationMode = false;
            this.rapidKeyPressCount = 0;
            if (this.rapidNavigationCooldown) {
                clearTimeout(this.rapidNavigationCooldown);
                this.rapidNavigationCooldown = null;
            }
        }
        // Always clear queue when forcing exit/transitioning to prevent stale commands
        this.clearNavigationQueue();
    }

    clearNavigationQueue() {
        if (this.navigationQueue.length > 0) {
            if (NavigationController.LOGGING_ENABLED) console.log(`NC: Clearing ${this.navigationQueue.length} queued commands`);
            this.navigationQueue = [];
        }
    }
    
    handleRapidGridNavigation(direction) {
        console.log(`NC: Rapid navigation ${direction}`);
        const newPos = { ...this.currentPosition };
        switch (direction) {
            case 'up':
                newPos.row = Math.max(0, newPos.row - 1);
                break;
            case 'down':
                newPos.row = Math.min(this.gridSize.rows - 1, newPos.row + 1);
                break;
            case 'left':
                newPos.col = Math.max(0, newPos.col - 1);
                break;
            case 'right':
                newPos.col = Math.min(this.gridSize.cols - 1, newPos.col + 1);
                break;
        }
        
        // Check if position actually changed
        if (newPos.row === this.currentPosition.row && newPos.col === this.currentPosition.col) {
            // Hit boundary - quick bounce
            if (this.components.contactSheet) {
                this.components.contactSheet.showQuickBounce(direction);
            }
            return false;
        }
        
        // Instant jump to new position - bypass ALL transitions
        this.currentPosition = newPos;
        this.isTransitioning = false; // Force transition flag off
        if (this.components.contactSheet) {
            // Don't change state, just update position instantly
            this.components.contactSheet.instantJumpToPosition(newPos);
        }
        
        // Update state callback for Router
        if (this._stateChangeCallback) {
            const imageName = this.components.contactSheet?.getImageNameByPosition(newPos.row, newPos.col) || 'unknown';
            this._stateChangeCallback(this.state, { row: newPos.row, col: newPos.col, imageName });
        }
        
        return true;
    }

    handleGridNavigation(direction) {
        const newPos = { ...this.currentPosition };
        switch (direction) {
            case 'up':
                newPos.row = Math.max(0, newPos.row - 1);
                break;
            case 'down':
                newPos.row = Math.min(this.gridSize.rows - 1, newPos.row + 1);
                break;
            case 'left':
                newPos.col = Math.max(0, newPos.col - 1);
                break;
            case 'right':
                newPos.col = Math.min(this.gridSize.cols - 1, newPos.col + 1);
                break;
        }
        // Check if position actually changed
        if (newPos.row === this.currentPosition.row && newPos.col === this.currentPosition.col) {
            // Hit boundary - trigger bounce animation
            const directionKeyMap = {
                'up': 'ArrowUp',
                'down': 'ArrowDown',
                'left': 'ArrowLeft',
                'right': 'ArrowRight'
            };
            this.components.contactSheet?.showBounceAnimation?.(directionKeyMap[direction]);
            return false;
        }
        // Delegate to ContactSheet, which will call updatePosition after animation
        this.components.contactSheet?.navigateToAdjacentImage(direction, newPos);
        return true;
    }

    handleLinearNavigation(direction) {
        // Linear navigation for detail view
        const currentLinear = this.gridToLinear(this.currentPosition.row, this.currentPosition.col);
        let newLinear = currentLinear;

        switch (direction) {
            case 'up':
            case 'left':
                newLinear = Math.max(0, currentLinear - 1);
                break;
            case 'down':
            case 'right':
                newLinear = Math.min(this.totalImages - 1, currentLinear + 1);
                break;
        }

        if (newLinear === currentLinear) {
            // Hit boundary - trigger DetailView bounce feedback
            const atFirst = currentLinear === 0;
            const atLast = currentLinear === this.totalImages - 1;
            // Map attempted direction to intended linear direction
            const attemptedPrevious = direction === 'up' || direction === 'left';
            const attemptedNext = direction === 'down' || direction === 'right';

            if (this.components.detailView?.showLinearBounceAnimation) {
                if (attemptedPrevious && atFirst) {
                    this.components.detailView.showLinearBounceAnimation('previous');
                } else if (attemptedNext && atLast) {
                    this.components.detailView.showLinearBounceAnimation('next');
                }
            }
            return false;
        }

        const newPos = this.linearToGrid(newLinear);
        this.currentPosition = newPos;
        
        // Update position with imageName for Router
        const imageName = this.components.contactSheet?.getImageNameByPosition(newPos.row, newPos.col) || 'unknown';
        if (this._stateChangeCallback) {
            this._stateChangeCallback(this.state, { row: newPos.row, col: newPos.col, imageName });
        }
        // After URL update, refresh page title and social meta for the new image in DETAIL_VIEW
        this.updatePageTitle();
        
        // Delegate to DetailView - NavigationController manages the position
        this.components.detailView?.navigateLinear?.(direction === 'up' || direction === 'left' ? 'previous' : 'next');
        
        return true;
    }

    gridToLinear(row, col) {
        return row * this.gridSize.cols + col;
    }

    linearToGrid(linear) {
        return {
            row: Math.floor(linear / this.gridSize.cols),
            col: linear % this.gridSize.cols
        };
    }

    async handleZoomIn() {
        if (this.state === 'OVERVIEW') {
            // Guard against rapid re-entry (e.g., double tap bursts)
            if (this.isTransitioning) return false;
            
            this.forceExitRapidNavigationMode();
            this.clearNavigationQueue(); // Ensure clean slate
            this.isTransitioning = true;
            this._startTransitionTimeout();
            // Use updateState to ensure routing + meta order consistency
            this.updateState('ZOOMED_IN');
            try {
                const imagePos = this.components.contactSheet?.layout?.getImagePosition?.(this.currentPosition.row, this.currentPosition.col);
                if (imagePos) {
                    await this.components.contactSheet?.zoomToImage(imagePos, this.currentPosition.row, this.currentPosition.col);
                }
                return true;
            } finally {
                this.isTransitioning = false;
                this._clearTransitionTimeout();
            }
        }
        return false;
    }

    async handleZoomOut() {
        if (this.isTransitioning) return false;
        
        this.forceExitRapidNavigationMode();
        this.clearNavigationQueue(); // Ensure clean slate
        
        if (this.state === 'ZOOMED_IN') {
            console.log('NC: navigate(\'zoom-out\') from zoomed.');
            this.isTransitioning = true;
            this._startTransitionTimeout();
            // Use updateState to ensure routing + meta order consistency
            this.updateState('OVERVIEW');
            try {
                await this.components.contactSheet?.zoomOut();
                return true;
            } finally {
                this.isTransitioning = false;
                this._clearTransitionTimeout();
            }
        } else if (this.state === 'DETAIL_VIEW') {
            console.log('NC: navigate(\'zoom-out\') from detail.');
            this.isTransitioning = true;
            this._startTransitionTimeout();
            // Use updateState to ensure routing + meta order consistency
            this.updateState('ZOOMED_IN');
            try {
                await this.components.detailView?.hide();
                return true;
            } finally {
                this.isTransitioning = false;
                this._clearTransitionTimeout();
            }
        }
        return false;
    }

    async handleDetailView() {
        if (this.state === 'ZOOMED_IN') {
            // Guard against re-entrancy during detail transition
            if (this.isTransitioning) return false;
            
            this.forceExitRapidNavigationMode();
            this.clearNavigationQueue(); // Ensure clean slate
            this.isTransitioning = true;
            this._startTransitionTimeout();
            // Use updateState to ensure routing + meta order consistency
            this.updateState('DETAIL_VIEW');
            try {
                await this.components.contactSheet?.showDetailView();
                return true;
            } finally {
                this.isTransitioning = false;
                this._clearTransitionTimeout();
            }
        }
        return false;
    }

    setState(newState) {
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController: ${this.state} → ${newState}`);
        this.state = newState;
        
        // Update body class for CSS state-based styling
        document.body.className = newState.toLowerCase().replace('_', '-');
        
        // Do not update meta/title here; defer to updateState after URL push

        // Manage close button visibility based on state
        this.updateCloseButtonVisibility();

        // Show/hide info button based on state
        this.infoModal?.onStateChange(newState);

        // Update context menu state when state changes
        this.components.contactSheet?.updateContextMenuState?.();
    }
    
    updateCloseButtonVisibility() {
        const closeButton = this.components.contactSheet?.closeButton;
        if (!closeButton) return;
        
        gsap.killTweensOf(closeButton);
        
        if (this.state === 'ZOOMED_IN') {
            // Show close button in zoomed state
            gsap.to(closeButton, {
                opacity: 1,
                duration: 0.3,
                onStart: () => { closeButton.style.pointerEvents = 'auto'; }
            });
        } else {
            // Hide close button in overview or detail states
            gsap.to(closeButton, {
                opacity: 0,
                duration: 0.3,
                onComplete: () => { closeButton.style.pointerEvents = 'none'; }
            });
        }
    }

    // Ensure all UI elements are synchronized after state restoration
    syncUIElements() {
        this.updateCloseButtonVisibility();
        this.components.contactSheet?.updateContextMenuState?.();
        this.components.contactSheet?.validateStateSync?.();
        
        // Ensure proper image brightness for zoomed state
        if (this.state === 'ZOOMED_IN' && this.currentPosition) {
            this.components.contactSheet?.setImageBrightness?.(
                this.currentPosition.row, 
                this.currentPosition.col
            );
        }
    }

    getCurrentPosition() {
        return { ...this.currentPosition };
    }

    // Called by components when they change position
    updatePosition(row, col) {
        console.log('CALLED: updatePosition', row, col);
        this.currentPosition = { row, col };
        const imageName = this.components.contactSheet?.getImageNameByPosition(row, col) || 'unknown';
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController position: ${row}, ${col}, imageName: ${imageName}`);
        
        // First trigger route update (URL must reflect current image before meta updates)
        this.handleRouteChange('position-change', { position: { row, col, imageName } });
        if (this._stateChangeCallback) {
            this._stateChangeCallback(this.state, { row, col, imageName });
        }
        // Then update meta/title, after URL is in place
        this.updatePageTitle();
        // Try to process any queued navigations after this update
        this.flushNavigationQueue();
    }

    // Called by components when they change state
    updateState(newState) {
        console.log('CALLED: updateState', newState);
        const oldState = this.state;
        this.setState(newState);
        const imageName = this.components.contactSheet?.getImageNameByPosition(this.currentPosition.row, this.currentPosition.col) || 'unknown';
        // Check if we should zoom out after detail view closes
        if (this._shouldZoomOutAfterDetailClose && oldState === 'DETAIL_VIEW' && newState === 'ZOOMED_IN') {
            this._shouldZoomOutAfterDetailClose = false;
            this._zoomOutAfterDetailTimeout = setTimeout(() => {
                this._zoomOutAfterDetailTimeout = null;
                this.components.contactSheet?.zoomOut();
            }, 100);
        }
        // First update URL for state change
        this.handleRouteChange('state-change', { newState });
        if (this._stateChangeCallback) {
            this._stateChangeCallback(this.state, { row: this.currentPosition.row, col: this.currentPosition.col, imageName });
        }
        // Then update meta/title after URL reflects state
        this.updatePageTitle();
        // Try to process any queued navigations after this update
        this.flushNavigationQueue();
    }

    // Handle route changes for future routing integration
    handleRouteChange(action, data = {}) {
        if (NavigationController.LOGGING_ENABLED) console.log(`NavigationController: route change ${action}`, data);
        
        // Future routing implementation will use this method
        // For now, just log the route changes
        switch (action) {
            case 'detail-close':
                if (NavigationController.LOGGING_ENABLED) console.log(`Route: closing detail view, returning to zoomed state`);
                break;
            case 'position-change':
                if (NavigationController.LOGGING_ENABLED) console.log(`Route: position changed to ${data.position?.row}, ${data.position?.col}`);
                break;
            case 'state-change':
                if (NavigationController.LOGGING_ENABLED) console.log(`Route: state changed to ${data.newState}`);
                break;
        }
    }

    // Linear navigation with boundary checking (moved from ContactSheet)
    navigateWithWrapping(direction) {
        const currentLinearIndex = this.currentPosition.row * this.gridSize.cols + this.currentPosition.col;
        const totalImages = this.totalImages;
        let targetLinearIndex;
        
        if (direction === 'next') {
            targetLinearIndex = currentLinearIndex + 1;
            if (targetLinearIndex >= totalImages) {
                // Don't wrap past the last image
                this.components.contactSheet?.showBounceAnimation?.('ArrowRight');
                return;
            }
        } else if (direction === 'previous') {
            targetLinearIndex = currentLinearIndex - 1;
            if (targetLinearIndex < 0) {
                // Don't wrap before the first image
                this.components.contactSheet?.showBounceAnimation?.('ArrowLeft');
                return;
            }
        }
        
        // Convert linear index back to row/col using NavigationController's grid
        const targetRow = Math.floor(targetLinearIndex / this.gridSize.cols);
        const targetCol = targetLinearIndex % this.gridSize.cols;
        
        // Update NavigationController position
        this.currentPosition = { row: targetRow, col: targetCol };
        
        const targetImage = { row: targetRow, col: targetCol };
        this.components.contactSheet?.moveToImage(targetImage);
    }

    // Context menu navigation handlers
    handleZoomToImage(options = {}) {
        // Don't duplicate log if already logged by keyboard handler
        if (options.source !== 'keyboard') {
            console.log(`NC: navigate('zoom-to-image') from ${options.source || 'unknown'}.`);
        }
        
        if (this.state === 'OVERVIEW') {
            let targetImage = this.currentPosition;
            
            // If we have a direct position (from tap/click), use it directly
            if (options.position && typeof options.position.row === 'number' && typeof options.position.col === 'number') {
                targetImage = { row: options.position.row, col: options.position.col };
                this.currentPosition = targetImage;
            }
            // Otherwise, if we have screen coordinates (from context menu), find the image at that position
            else if (options.position && options.position.x && options.position.y && this.components.contactSheet) {
                // Convert screen coordinates to pointer coordinates for raycasting
                this.components.contactSheet.pointer.x = (options.position.x / window.innerWidth) * 2 - 1;
                this.components.contactSheet.pointer.y = -(options.position.y / window.innerHeight) * 2 + 1;
                this.components.contactSheet.raycaster.setFromCamera(this.components.contactSheet.pointer, this.components.contactSheet.camera);
                
                const imageAtPointer = this.components.contactSheet.getImageAtPointer();
                if (imageAtPointer) {
                    targetImage = imageAtPointer;
                    this.currentPosition = targetImage;
                }
            }
            
            const imagePos = this.components.contactSheet?.layout?.getImagePosition(targetImage.row, targetImage.col);
            if (imagePos) {
                this.infoModal?.hideButtonImmediately();
                this.components.contactSheet?.zoomToImage(imagePos, targetImage.row, targetImage.col);
                return true;
            }
        }
        return false;
    }

    async handleOpenDetail(options = {}) {
        if (NavigationController.LOGGING_ENABLED) console.log('NavigationController: handling open detail');
        
        // Guard against re-entrancy
        if (this.isTransitioning) return false;

        if (this.state === 'OVERVIEW') {
            this.forceExitRapidNavigationMode();
            this.clearNavigationQueue(); // Ensure clean slate
            this.isTransitioning = true;
            this._startTransitionTimeout();

            try {
                // First zoom to the image, then show detail view
                let targetImage = this.currentPosition;

                // If we have position data from the context menu, find the image at that position
                if (options.position && options.position.x && options.position.y && this.components.contactSheet) {
                    this.components.contactSheet.pointer.x = (options.position.x / window.innerWidth) * 2 - 1;
                    this.components.contactSheet.pointer.y = -(options.position.y / window.innerHeight) * 2 + 1;
                    this.components.contactSheet.raycaster.setFromCamera(this.components.contactSheet.pointer, this.components.contactSheet.camera);

                    const imageAtPointer = this.components.contactSheet.getImageAtPointer();
                    if (imageAtPointer) {
                        targetImage = imageAtPointer;
                        this.currentPosition = targetImage;
                    }
                }

                // Set a flag to show detail view after zoom completes
                if (this.components.contactSheet) {
                    this.components.contactSheet.shouldShowDetailAfterZoom = true;
                    const imagePos = this.components.contactSheet.layout?.getImagePosition(targetImage.row, targetImage.col);
                    if (imagePos) {
                        console.log('NC: Step 2/3: zoom-in');
                        await this.components.contactSheet.zoomToImage(imagePos, targetImage.row, targetImage.col);
                        return true;
                    }
                }
            } finally {
                this.isTransitioning = false;
                this._clearTransitionTimeout();
            }
        } else if (this.state === 'ZOOMED_IN') {
            this.forceExitRapidNavigationMode();
            this.clearNavigationQueue(); // Ensure clean slate
            this.isTransitioning = true;
            this._startTransitionTimeout();
            try {
                await this.components.contactSheet?.showDetailView();
                return true;
            } finally {
                this.isTransitioning = false;
                this._clearTransitionTimeout();
            }
        }
        return false;
    }

    handlePreviousImage() {
        console.log('NC: navigate(\'previous-image\').');
        
        if (this.state === 'ZOOMED_IN') {
            this.navigateWithWrapping('previous');
            return true;
        } else if (this.state === 'DETAIL_VIEW') {
            // Use proper linear navigation for detail view
            return this.handleLinearNavigation('left');
        }
        return false;
    }

    handleNextImage() {
        console.log('NC: navigate(\'next-image\').');
        
        if (this.state === 'ZOOMED_IN') {
            this.navigateWithWrapping('next');
            return true;
        } else if (this.state === 'DETAIL_VIEW') {
            // Use proper linear navigation for detail view
            return this.handleLinearNavigation('right');
        }
        return false;
    }

    async handleCloseDetail(options = {}) {
        if (options.source === 'pinch') {
            console.log(`NC: navigate('close-detail') from pinch.`);
        } else if (options.position) {
            console.log(`NC: navigate('close-detail') from context menu.`);
        } else {
            console.log(`NC: navigate('close-detail').`);
        }
        if (NavigationController.LOGGING_ENABLED) console.log('NavigationController: handling close detail');
        
        // Guard against re-entrancy
        if (this.isTransitioning) return false;

        this.forceExitRapidNavigationMode();
        this.clearNavigationQueue(); // Ensure clean slate

        if (this.state === 'DETAIL_VIEW') {
            this.isTransitioning = true;
            this._startTransitionTimeout();
            try {
                await this.components.detailView?.hide();
                return true;
            } finally {
                this.isTransitioning = false;
                this._clearTransitionTimeout();
            }
        }
        return false;
    }

    async handleBackToContact() {
        console.log(`NC: navigate('back-to-contact').`);
        if (NavigationController.LOGGING_ENABLED) console.log('NavigationController: handling back to contact');
        
        // Guard against re-entrancy
        if (this.isTransitioning) return false;

        this.forceExitRapidNavigationMode();
        this.clearNavigationQueue(); // Ensure clean slate

        if (this.state === 'DETAIL_VIEW') {
            // Set flag to zoom out after detail view closes
            this._shouldZoomOutAfterDetailClose = true;
            this.isTransitioning = true;
            this._startTransitionTimeout();
            try {
                await this.components.detailView?.hide();
                return true;
            } finally {
                this.isTransitioning = false;
                this._clearTransitionTimeout();
            }
        } else if (this.state === 'ZOOMED_IN') {
            return this.handleZoomOut();
        }
        return false;
    }

    async navigateToPosition(row, col) {
        console.log(`NC: navigateToPosition(${row},${col}).`);

        const currentPos = this.getCurrentPosition();
        if (row === currentPos.row && col === currentPos.col) return Promise.resolve(true); // Already there

        if (this.state === 'ZOOMED_IN') {
            if (this.components.contactSheet) {
                return this.components.contactSheet.moveToImage({ row, col });
            }
        } else if (this.state === 'DETAIL_VIEW') {
            // For detail view, we need to update position and transition the detail view
            this.currentPosition = { row, col };
            
            // Update position with imageName for Router
            const imageName = this.components.contactSheet?.getImageNameByPosition(row, col) || 'unknown';
            if (this._stateChangeCallback) {
                this._stateChangeCallback(this.state, { row, col, imageName });
            }
            // After URL update, refresh page title and social meta for the new image in DETAIL_VIEW
            this.updatePageTitle();
            
            // Use ContactSheet's method to smoothly transition detail view to new image
            if (this.components.contactSheet) {
                // Determine direction for animation
                const currentLinear = currentPos.row * this.gridSize.cols + currentPos.col;
                const targetLinear = row * this.gridSize.cols + col;
                
                // Determine keyPressed based on navigation direction
                let keyPressed;
                if (targetLinear > currentLinear) {
                    keyPressed = 'ArrowRight';
                } else {
                    keyPressed = 'ArrowLeft';
                }
                
                // Use horizontal direction for detail view transitions
                await this.components.contactSheet.updateDetailViewToNewImage({ row, col }, 'horizontal', keyPressed);
                return Promise.resolve(true);
            }
        } else {
            if (NavigationController.LOGGING_ENABLED) console.warn(`navigateToPosition called in invalid state: ${this.state}`);
        }

        return Promise.resolve(false);
    }

    registerStateChangeCallback(callback) {
        this._stateChangeCallback = callback;
    }

    getPositionByImageName(imageName) {
        if (!imageName || typeof imageName !== 'string') {
            if (NavigationController.LOGGING_ENABLED) console.warn('getPositionByImageName called with invalid imageName:', imageName);
            return null;
        }
        
        if (!this.components.contactSheet || !this.components.contactSheet.imageMapping) {
            if (NavigationController.LOGGING_ENABLED) console.warn('getPositionByImageName called before imageMapping is available.');
            return null;
        }
        const mapping = this.components.contactSheet.imageMapping;
        for (let row = 0; row < mapping.length; row++) {
            for (let col = 0; col < mapping[row].length; col++) {
                if (mapping[row][col] === imageName) {
                    return { row, col };
                }
            }
        }
        return null; // Not found
    }

    async updatePageTitle() {
        // Bump token to invalidate any in-flight async writes from previous calls
        const metaToken = ++this._metaUpdateToken;
        const siteName = 'Burton Rast Photography';
        const activeSheetId = this.components.contactSheet?.sheetId || 'sosf';
        const currentImageName = this.components.contactSheet?.getImageNameByPosition(this.currentPosition.row, this.currentPosition.col);

        // OVERVIEW: reset title and base meta
        if (this.state === 'OVERVIEW') {
            document.title = siteName;
            const pageUrl = `${window.location.origin}/${activeSheetId}`;
            // Guard against stale writes
            if (metaToken !== this._metaUpdateToken) return;
            updateSocialMetaTags({
                siteName,
                sheetId: activeSheetId,
                imageData: null,
                pageUrl,
                state: this.state,
                imageWidth: 1200,
                imageHeight: 630
            });
            return;
        }

        // Prefer DetailView data ONLY if it matches current position's filename
        const currentDetailData = this.components.detailView?.currentImageData;
        const detailFilename = currentDetailData?.filename;
        const detailMatchesPosition = !!detailFilename && !!currentImageName && (
            detailFilename === currentImageName ||
            detailFilename.replace(/\.[^/.]+$/, '') === currentImageName.replace(/\.[^/.]+$/, '')
        );
        if (currentDetailData && detailMatchesPosition && (this.state === 'DETAIL_VIEW' || this.state === 'ZOOMED_IN')) {
            // Title
            if (this.state === 'ZOOMED_IN') {
                document.title = currentDetailData.metadata?.title || currentDetailData.filename || siteName;
            } else if (this.state === 'DETAIL_VIEW') {
                const baseTitle = currentDetailData.metadata?.title || currentDetailData.filename || siteName;
                document.title = baseTitle + ' - Image Details';
            }

            // URL formatting mirrors Router.formatUrl
            const imageNameNoExt = (currentDetailData.filename || '').replace(/\.[^/.]+$/, '');
            let path = `/${activeSheetId}/${imageNameNoExt}`;
            if (this.state === 'DETAIL_VIEW') path += '/detail';
            const pageUrl = `${window.location.origin}${path}`;

            // Guard against stale writes
            if (metaToken !== this._metaUpdateToken) return;
            updateSocialMetaTags({
                siteName,
                sheetId: activeSheetId,
                imageData: {
                    filename: currentDetailData.filename,
                    title: currentDetailData.metadata?.title || currentDetailData.filename,
                    description: currentDetailData.metadata?.description,
                    altText: currentDetailData.metadata?.altText
                },
                pageUrl,
                state: this.state,
                imageWidth: 1200,
                imageHeight: 630
            });
            return;
        }

        // Immediate update using current position filename (works even if DetailView is mid-transition)
        const imageName = currentImageName;
        if (imageName) {
            // Immediate, synchronous tag write using filename (no awaits)
            const nameWithoutExt = imageName.replace(/\.[^/.]+$/, '');
            const fallbackTitle = generateTitleFromFilename(imageName);
            let path = `/${activeSheetId}/${nameWithoutExt}`;
            if (this.state === 'DETAIL_VIEW') path += '/detail';
            const pageUrl = `${window.location.origin}${path}`;

            // Update title immediately
            if (this.state === 'ZOOMED_IN') {
                document.title = fallbackTitle;
            } else if (this.state === 'DETAIL_VIEW') {
                document.title = `${fallbackTitle} - Image Details`;
            }

            // Immediate social tags (ensures share sheets have an image even if pressed instantly)
            // Guard against stale writes
            if (metaToken !== this._metaUpdateToken) return;
            updateSocialMetaTags({
                siteName,
                sheetId: activeSheetId,
                imageData: {
                    filename: imageName,
                    title: fallbackTitle,
                    description: undefined,
                    altText: undefined
                },
                pageUrl,
                state: this.state,
                imageWidth: 1200,
                imageHeight: 630
            });
        }

        // Then refine with metadata if available (async)
        if (!imageName) {
            document.title = siteName;
            const pageUrl = `${window.location.origin}/${activeSheetId}`;
            updateSocialMetaTags({ siteName, sheetId: activeSheetId, imageData: null, pageUrl, state: 'OVERVIEW', imageWidth: 1200, imageHeight: 630 });
            return;
        }

        try {
            const sheetData = await this.components.contactSheet?.dataLoader?.loadSheetData(activeSheetId);
            if (metaToken !== this._metaUpdateToken) return;
            const imageData = sheetData?.images?.find(img => img.filename === imageName);

            if (imageData) {
                // Title
                if (this.state === 'ZOOMED_IN') {
                    document.title = imageData.title;
                } else if (this.state === 'DETAIL_VIEW') {
                    document.title = imageData.title + ' - Image Details';
                }

                // URL formatting mirrors Router.formatUrl
                const nameWithoutExt = imageName.replace(/\.[^/.]+$/, '');
                let path = `/${activeSheetId}/${nameWithoutExt}`;
                if (this.state === 'DETAIL_VIEW') path += '/detail';
                const pageUrl = `${window.location.origin}${path}`;

                // Guard against stale writes
                if (metaToken !== this._metaUpdateToken) return;
                updateSocialMetaTags({
                    siteName,
                    sheetId: activeSheetId,
                    imageData: {
                        filename: imageData.filename,
                        title: imageData.title,
                        description: imageData.description,
                        altText: imageData.altText
                    },
                    pageUrl,
                    state: this.state,
                    imageWidth: 1200,
                    imageHeight: 630
                });
            } else {
                document.title = siteName;
                const pageUrl = `${window.location.origin}/${activeSheetId}`;
                if (metaToken !== this._metaUpdateToken) return;
                updateSocialMetaTags({ siteName, sheetId: activeSheetId, imageData: null, pageUrl, state: 'OVERVIEW', imageWidth: 1200, imageHeight: 630 });
            }
        } catch (error) {
            console.warn('Failed to load sheet data for title/meta update:', error);
            document.title = siteName;
            const pageUrl = `${window.location.origin}/${activeSheetId}`;
            if (metaToken !== this._metaUpdateToken) return;
            updateSocialMetaTags({ siteName, sheetId: activeSheetId, imageData: null, pageUrl, state: 'OVERVIEW', imageWidth: 1200, imageHeight: 630 });
        }
    }

    // Handle tutorial interaction - mark current tutorial as learned
    handleTutorialInteraction() {
        this.components.contactSheet?.markCurrentTutorialLearned?.();
    }
}
